/**
 * Chill Garden Anti-Scrape Shield
 * Blocks bot/crawler scraping of images, text, and prices
 * while preserving SEO (Google) and AI SEO (ChatGPT, Perplexity, Gemini, etc.)
 *
 * How it works:
 * - Search engines and AI crawlers render JS, so they see all content normally
 * - Simple HTTP scrapers (curl, requests, BeautifulSoup) can't execute JS
 * - Headless browser scrapers get detected and blocked
 * - Real users can interact with everything normally (copy, save, right-click)
 */

(function () {
  'use strict';

  var SHIELD_VERSION = '1.1.1';
  var DEBUG = false;

  // === 0. KNOWN GOOD BOT ALLOWLIST ===
  // Preventive — these UA fingerprints are always allowed through regardless of headless signals.
  // Updated 2026-05-30 to harden against Googlebot eventually triggering >=3 signals as Chrome evolves.
  function isAllowedBot() {
    var ua = navigator.userAgent || '';
    var allowed = [
      /Googlebot/i,           // Google Search crawler
      /Googlebot-Image/i,
      /Googlebot-News/i,
      /Googlebot-Video/i,
      /APIs-Google/i,
      /AdsBot-Google/i,
      /Mediapartners-Google/i,
      /Google-InspectionTool/i,  // Search Console URL Inspection
      /Storebot-Google/i,
      /Bingbot/i,             // Microsoft Bing
      /BingPreview/i,
      /Slurp/i,               // Yahoo
      /DuckDuckBot/i,         // DuckDuckGo
      /Baiduspider/i,         // Baidu (HK relevant for ZH content)
      /YandexBot/i,
      /Applebot/i,            // Apple Spotlight / Siri
      /ChatGPT-User/i,        // OpenAI on-demand fetches
      /OAI-SearchBot/i,       // OpenAI SearchGPT
      /GPTBot/i,              // OpenAI training crawler
      /PerplexityBot/i,       // Perplexity
      /ClaudeBot/i,           // Anthropic
      /Claude-Web/i,
      /anthropic-ai/i,
      /Google-Extended/i,     // Gemini training
      /CCBot/i,               // Common Crawl (feeds many LLMs)
      /facebookexternalhit/i, // Open Graph fetcher
      /Twitterbot/i,
      /LinkedInBot/i,
      /WhatsApp/i,
      /Telegram/i,
      // PageSpeed Insights / Lighthouse lab (client-side shield otherwise blocks webdriver/HeadlessChrome)
      /Chrome-Lighthouse/i,
      /PageSpeed Insights/i,
      /Google Page Speed Insights/i,
    ];
    for (var i = 0; i < allowed.length; i++) {
      if (allowed[i].test(ua)) return true;
    }
    return false;
  }

  // === 1. HEADLESS BROWSER DETECTION ===
  // Detect automated browsers (Puppeteer, Playwright, Selenium)
  // Known good bots (Google, AI crawlers) use their own rendering engines and won't trigger these

  function detectHeadless() {
    var signals = 0;

    // navigator.webdriver is set by Selenium/Puppeteer/Playwright
    if (navigator.webdriver === true) signals += 3;

    // Headless Chrome has no plugins
    if (navigator.plugins && navigator.plugins.length === 0 && !/Mobile|Android/i.test(navigator.userAgent)) signals += 1;

    // Missing language
    if (!navigator.language && !navigator.languages) signals += 2;

    // Chrome headless specific
    if (/HeadlessChrome/i.test(navigator.userAgent)) signals += 3;

    // PhantomJS
    if (window.callPhantom || window._phantom) signals += 3;

    // Nightmare.js
    if (window.__nightmare) signals += 3;

    // Check for missing browser features that real browsers always have
    if (!window.chrome && /Chrome/i.test(navigator.userAgent) && !/Edge|Edg/i.test(navigator.userAgent)) signals += 1;

    // Puppeteer automation flag
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'notifications' }).then(function (result) {
        if (result.state === 'prompt' && Notification.permission === 'denied') {
          signals += 2;
        }
      }).catch(function () {});
    }

    return signals >= 3;
  }

  // === 2. BOT BLOCKER ===
  // When a headless browser is detected, hide the real content and show a message

  function blockBot() {
    // Replace page content so the scraper gets nothing useful
    var overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:#fff;z-index:999999;display:flex;align-items:center;justify-content:center;flex-direction:column;font-family:sans-serif;';
    overlay.innerHTML = '<h1 style="color:#333;">Access Denied</h1><p style="color:#666;">Automated access to this site is not permitted.</p>';
    document.body.innerHTML = '';
    document.body.appendChild(overlay);

    // Remove all product data from the DOM
    var metas = document.querySelectorAll('meta[property*="product"], script[type="application/ld+json"]');
    metas.forEach(function (el) { el.remove(); });
  }

  // === 3. CONTENT OBFUSCATION FOR SIMPLE SCRAPERS ===
  // Decode obfuscated content that Liquid encoded via data-cg-s attributes
  // Real browsers (including Googlebot, AI crawlers) run JS and decode it
  // Simple HTTP scrapers (curl, requests) see only the encoded version

  function decodeShieldedContent() {
    var elements = document.querySelectorAll('[data-cg-s]');
    elements.forEach(function (el) {
      try {
        var decoded = atob(el.getAttribute('data-cg-s'));
        el.textContent = decoded;
        el.removeAttribute('data-cg-s');
      } catch (e) {
        // Ignore decode errors
      }
    });
  }

  // === 4. HONEYPOT TRAP ===
  // Hidden elements that only scrapers will find and collect
  // If this fake data appears on another site, you know they scraped you

  function addHoneypots() {
    var honeypot = document.createElement('div');
    honeypot.setAttribute('aria-hidden', 'true');
    // inert + tabindex=-1: keep the trap invisible to keyboards, screen readers
    // and AI agents (fixes aria-hidden-focus a11y violation); DOM scrapers still see it
    honeypot.setAttribute('inert', '');
    honeypot.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;';
    honeypot.className = 'product-price-data';
    honeypot.innerHTML = '<span class="price-item" data-product-price>$0.00</span><a href="/honeypot-trap" class="product-link" tabindex="-1">Fake Product</a>';
    document.body.appendChild(honeypot);
  }

  // === 5. CONSOLE WARNING ===

  function addConsoleWarning() {
    if (typeof console !== 'undefined') {
      console.log(
        '%c⚠️ Chill Garden Content Protection',
        'color: #e74c3c; font-size: 18px; font-weight: bold;'
      );
      console.log(
        '%cAutomated scraping of this site is monitored and prohibited.\nImages, prices, and product data are proprietary.',
        'color: #333; font-size: 13px;'
      );
    }
  }

  // === 6. MUTATION OBSERVER ===
  // Re-apply content decoding when dynamic content loads (AJAX pagination, quick-add modals)

  function observeDynamicContent() {
    if (!window.MutationObserver) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1 && node.querySelector) {
            var shielded = node.querySelectorAll('[data-cg-s]');
            shielded.forEach(function (el) {
              try {
                el.textContent = atob(el.getAttribute('data-cg-s'));
                el.removeAttribute('data-cg-s');
              } catch (e) {}
            });
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // === INIT ===

  function init() {
    // Skip protection in Shopify theme editor
    if (window.Shopify && window.Shopify.designMode) {
      if (DEBUG) console.log('[CG Shield] Disabled in theme editor');
      return;
    }

    // Allowlist: known good bots (Google, AI crawlers, social fetchers) — never block, always decode content
    if (isAllowedBot()) {
      if (DEBUG) console.log('[CG Shield] Allowlisted bot — decoding without honeypot');
      decodeShieldedContent();
      observeDynamicContent();
      return;
    }

    // Detect and block headless browsers (scraper bots)
    if (detectHeadless()) {
      if (DEBUG) console.log('[CG Shield] Headless browser detected — blocking');
      blockBot();
      return;
    }

    // For real browsers: decode content, add honeypots, observe dynamic loads
    decodeShieldedContent();
    addHoneypots();
    addConsoleWarning();
    observeDynamicContent();

    if (DEBUG) console.log('[CG Shield] v' + SHIELD_VERSION + ' initialized');
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
